import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
  throw new Error('Configuration error: Missing Supabase credentials');
}

// Silence console errors about WebSocket
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  if (args[0]?.toString?.().includes('WebSocket')) return;
  originalConsoleError(...args);
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'supabase.auth.token',
    // Disable automatic session refresh on mobile to prevent hangs
    flowType: 'pkce',
  },
  realtime: {
    // Disable realtime on mobile to prevent WebSocket hangs
    params: {
      eventsPerSecond: 2,
    },
    timeout: 5000,
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web'
    },
    // Add fetch options with timeout
    fetch: (url, options = {}) => {
      return Promise.race([
        fetch(url, {
          ...options,
          // Add keepalive to prevent connection hanging on mobile
          keepalive: false,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        ),
      ]) as Promise<Response>;
    },
  }
});

export async function getUserId(): Promise<string> {
  const impersonatedUserId = sessionStorage.getItem('impersonated_user_id');
  if (impersonatedUserId) {
    return impersonatedUserId;
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  return user.id;
}

export type Category = {
  id: string;
  name: string;
  type: 'income' | 'expense';
  is_internal_transfer: boolean;
  user_id?: string;
  created_at: string;
};

export type Transaction = {
  id: string;
  date: string;
  label: string;
  amount: number;
  category_id: string | null;
  notes: string;
  imported_from: string;
  is_internal_transfer: boolean;
  created_at: string;
};

export type Scenario = {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
};

export type ScenarioBudgetLine = {
  id: string;
  scenario_id: string;
  budget_line_id: string;
  is_included: boolean;
  adjusted_amount: number | null;
  created_at: string;
};

export type ScenarioCustomOperation = {
  id: string;
  scenario_id: string;
  label: string;
  amount: number;
  category_id: string | null;
  notes: string;
  created_at: string;
};

export type BudgetLine = {
  id: string;
  category_id: string;
  year: number;
  planned_amount: number;
  notes: string;
  created_at: string;
  source_module?: string | null;
};

export type BudgetAllocation = {
  id: string;
  budget_line_id: string;
  transaction_id: string;
  allocated_amount: number;
  created_at: string;
};

export type CashFlowForecast = {
  id: string;
  year: number;
  month: number;
  forecast_encaissements: number;
  forecast_decaissements: number;
  scenario_id: string | null;
  initial_treasury?: number;
  created_at: string;
  updated_at: string;
};

export type CashFlowForecastLine = {
  id: string;
  forecast_year: number;
  forecast_month: number;
  description: string;
  amount: number;
  category_id: string | null;
  type: 'income' | 'expense';
  scenario_id: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
};

export type TreasuryScenario = {
  id: string;
  user_id: string;
  name: string;
  description: string;
  year: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type TransactionPattern = {
  id: string;
  user_id: string;
  keyword: string;
  category_id: string;
  confidence_score: number;
  created_at: string;
  updated_at: string;
};

export type CategorizationSuggestion = {
  id: string;
  user_id: string;
  transaction_id: string;
  suggested_category_id: string;
  confidence_score: number;
  was_accepted: boolean | null;
  feedback_at: string | null;
  created_at: string;
};

export type CategorySuggestion = {
  category_id: string;
  category_name: string;
  confidence_score: number;
};
